import React, { useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  IconButton,
  Link as MuiLink
} from "@mui/material";
import { useDispatch } from "react-redux";
import DownloadIcon from "@mui/icons-material/Download";
import FooterCommonLogin from "./footerCommonLogin";
import type { AppDispatch } from "@store/store";
import {
  fetchGuidelines,
  getPublicUrlThunk
} from "../redux/thunk/guidelinesThunk";
import type { GuidelineApiItem } from "../services/apiEndpoints";
import dayjs from "dayjs";

const accent = "#3A1C73";
const border = "#E4E4E4";
const subdued = "#9E9E9E";

const GuidelineCard = ({
  index,
  date,
  published_on_date,
  title,
  href,
  onDownload,
  disabled
}: {
  index: number;
  date: string;
  published_on_date?: string;
  title: React.ReactNode;
  href?: string;
  onDownload?: () => void | Promise<void>;
  disabled?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.5, sm: 2, md: 2.5 },
      borderRadius: 2,
      border: `1px solid ${border}`,
      mb: { xs: 1, md: 2 }
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={{ xs: 1, sm: 2 }}
    >
      <Typography
        sx={{
          minWidth: 28,
          color: subdued,
          fontWeight: 600,
          fontSize: { xs: 13, sm: 15 }
        }}
      >
        {index}.
      </Typography>

      <Stack flex={1} spacing={0.5}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 13, sm: 12 },
            lineHeight: "20px",
            color: "#3A1C73"
          }}
        >
           Published On Date: {dayjs(published_on_date).format("DD/MM/YYYY")}
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: { xs: 13, sm: 14 },
            lineHeight: "20px"
          }}
        >
          {title}
        </Typography>
        {published_on_date && (
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 12, sm: 12 },
              lineHeight: "18px",
              color: "#3A1C73"
            }}
          >
            Updated On Date: {date}
          </Typography>
        )}
      </Stack>

      {href ? (
        <MuiLink
          href={href}
          target="_blank"
          rel="noopener"
          sx={{ display: "inline-flex" }}
        >
          <IconButton
            aria-label="Download Pdf"
            disabled={disabled}
            sx={{
              width: { xs: 32, sm: 35 },
              height: { xs: 32, sm: 35 },
              border: `1px solid ${accent}`,
              color: accent,
              mt: { xs: 1, sm: 0 }
            }}
          >
            <DownloadIcon />
          </IconButton>
        </MuiLink>
      ) : (
        <IconButton
          aria-label="Download Pdf"
          onClick={onDownload}
          disabled={disabled || !onDownload}
          sx={{
            width: { xs: 32, sm: 35 },
            height: { xs: 32, sm: 35 },
            border: `1px solid ${accent}`,
            color: accent,
            mt: { xs: 1, sm: 0 }
          }}
        >
          <DownloadIcon />
        </IconButton>
      )}
    </Stack>
  </Paper>
);

const Guidelines: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = React.useState<"access" | "row">("access");
  const [generalItems, setGeneralItems] = React.useState<GuidelineApiItem[]>(
    []
  );
  const [tabItems, setTabItems] = React.useState<GuidelineApiItem[]>([]);
  const [status, setStatus] = React.useState<
    "idle" | "loading" | "succeeded" | "failed"
  >("idle");

  // const staticGeneral = [
  //   { date: "14 Jan 2003", title: "Control of NH Act", href: "/NHAct2002.pdf" }
  // ];

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const action = await dispatch(
          fetchGuidelines({ category: "General" }) as any
        );
        const payload = (action as any)?.payload as
          | GuidelineApiItem[]
          | undefined;
        if (mounted) setGeneralItems(Array.isArray(payload) ? payload : []);
      } catch {
        if (mounted) setGeneralItems([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setStatus("loading");
      try {
        const category = activeTab === "row" ? "Row" : "Access";
        const action = await dispatch(fetchGuidelines({ category }) as any);
        const payload = (action as any)?.payload as
          | GuidelineApiItem[]
          | undefined;
        if (mounted) {
          setTabItems(Array.isArray(payload) ? payload : []);
          setStatus("succeeded");
        }
      } catch {
        if (mounted) {
          setTabItems([]);
          setStatus("failed");
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeTab, dispatch]);

  const mapCategoryToModuleType = (category?: string, currentTab?: string) => {
    const c = String(category ?? currentTab ?? "").toLowerCase();
    if (c.includes("row")) return "row";
    if (c.includes("access")) return "access";
    if (c.includes("general")) return "row"; // map General -> row
    return "row";
  };

  const getObjectKey = (path?: string) => {
    if (!path) return undefined;
    return path.replace(/^morth-documents\/(row|access)\//, "");
  };

  const handleDownload = useCallback(
    async (item: GuidelineApiItem) => {
      if (!item?.document) return;
      try {
        const rawKey: string | undefined = item.document.key_name ?? undefined;
        const objectKey = getObjectKey(rawKey);
        const document_name: string | undefined =
          objectKey ?? item.document.document_name ?? undefined;

        if (!document_name) {
          console.warn("No file key or document_name available for download.");
          return;
        }

        const moduletype = mapCategoryToModuleType(item.category, activeTab);

        const payload: any = await dispatch(
          getPublicUrlThunk({ document_name, moduletype }) as any
        ).unwrap();

        if (!payload) return;

        // If API returned a public URL string, download/open it
        if (typeof payload === "string") {
          const a = document.createElement("a");
          a.href = payload;
          a.download = document_name?.split("-").pop() || "file";
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }

        // If payload is a Blob, trigger a download
        const blob: Blob = payload as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = document_name?.split("-").pop() || "file";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      }
    },
    [dispatch, activeTab]
  );

  const generalRenderList = generalItems.length ? generalItems : [];
  const tabRenderList = tabItems.length ? tabItems : [];

  return (
    <>
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.10)), url(${require("@assets/images/common-login-bg.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: { xs: 100, sm: 120, md: 140 },
          display: "flex",
          alignItems: "center"
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            width: { xs: "98%", sm: "95%", md: "95%" },
            py: { xs: 2, sm: 4, md: 6 }
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#FFF",
              fontWeight: 800,
              fontSize: { xs: 20, sm: 24, md: 28 },
              lineHeight: 1.25
            }}
          >
            Guidelines
          </Typography>
        </Container>
      </Box>

      <Box sx={{ bgcolor: "#F7F6FB" }}>
        <Container
          maxWidth={false}
          sx={{
            width: { xs: "98%", sm: "95%", md: "95%" },
            py: { xs: 2, sm: 4, md: 3 }
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              borderRadius: 2,
              bgcolor: "transparent"
            }}
          >
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#212121",
                  fontSize: { xs: 16, sm: 18 }
                }}
              >
                General Guidelines
              </Typography>

              {generalRenderList.length > 0 ? (
                generalRenderList.map((g, i) => (
                  <GuidelineCard
                    key={`gen-${g.id ?? i}`}
                    index={i + 1}
                    date={
                      g.created_at
                        ? new Date(g.created_at).toLocaleDateString()
                        : ""
                    }
                    published_on_date={g.published_on_date}
                    title={g.title ?? g.document?.document_name ?? "Untitled"}
                    onDownload={() => handleDownload(g)}
                    disabled={!g.document}
                  />
                ))
              ) : (
                <Typography>No guidelines found.</Typography>
              )}

              <Typography
                variant="h5"
                sx={{
                  mt: { xs: 2, sm: 3 },
                  mb: { xs: 2, sm: 3 },
                  fontWeight: 700,
                  fontSize: { xs: 15, sm: 18 },
                  color: "#212121"
                }}
              >
                Guidelines For Access and Right of Way Permission
              </Typography>

              <Box sx={{ mt: 1, position: "relative", pb: { xs: 1.5, sm: 2 } }}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  sx={{ width: "100%", position: "relative", zIndex: 1 }}
                >
                  <Box sx={{ width: "50%" }}>
                    <Typography
                      onClick={() => setActiveTab("access")}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: { xs: 14, sm: 16 },
                        color: activeTab === "access" ? accent : subdued,
                        textAlign: "left",
                        py: { xs: 0.5, sm: 1 }
                      }}
                    >
                      Access Permission Guidelines
                    </Typography>
                  </Box>
                  <Box sx={{ width: "50%" }}>
                    <Typography
                      onClick={() => setActiveTab("row")}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: { xs: 14, sm: 16 },
                        color: activeTab === "row" ? accent : subdued,
                        textAlign: "left",
                        py: { xs: 0.5, sm: 1 }
                      }}
                    >
                      Right of Way Permission Guidelines
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: { xs: "1px", sm: "1.5px" },
                    bgcolor: border,
                    zIndex: 0,
                    pointerEvents: "none"
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: activeTab === "access" ? 0 : "50%",
                    width: "50%",
                    height: { xs: "2px", sm: "2.5px" },
                    bgcolor: accent,
                    zIndex: 0,
                    pointerEvents: "none"
                  }}
                />
              </Box>

              <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1, sm: 2 } }}>
                {status === "loading" && <Typography>Loading...</Typography>}
                {status !== "loading" && tabRenderList.length === 0 && (
                  <Typography>No guidelines available.</Typography>
                )}
                {tabRenderList.map((g, i) => (
                  <GuidelineCard
                    key={`${activeTab}-${g.id ?? i}`}
                    index={i + 1}
                    date={
                      g.created_at
                        ? new Date(g.created_at).toLocaleDateString()
                        : ""
                    }
                    published_on_date={g.published_on_date}
                    title={g.title ?? g.document?.document_name ?? "Untitled"}
                    onDownload={() => handleDownload(g)}
                    disabled={!g.document}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <FooterCommonLogin />
    </>
  );
};

export default Guidelines;
